import { Lock, Sparkles } from 'lucide-react';
import type { ReactNode } from 'react';
import { useProContext } from '../pro';
import { PRO_FEATURES, type ProFeatureId } from '../lib/proFeatures';

type Props = {
  feature: ProFeatureId;
  children: ReactNode;
  preview?: ReactNode;
  /**
   * 'overlay' (default): renders children blurred behind a "Margin Pro"
   * chip. 'replace': renders the `preview` slot instead of children.
   */
  mode?: 'overlay' | 'replace';
  /** Optional className passed to the outer button so callers can size. */
  className?: string;
};

/**
 * Generic Pro gate. Renders children unobstructed when the user is on
 * Pro; otherwise overlays/replaces with a tappable affordance that
 * opens the paywall.
 */
export function ProGate({
  feature,
  children,
  preview,
  mode = 'overlay',
  className,
}: Props) {
  const pro = useProContext();
  const meta = PRO_FEATURES[feature];
  if (pro.isPro) return <>{children}</>;

  if (mode === 'replace' && preview !== undefined) {
    return (
      <button
        type="button"
        onClick={pro.openPaywall}
        aria-label={`${meta.title} · Margin Pro`}
        className={`block w-full text-left ${className ?? ''}`}
      >
        {preview}
      </button>
    );
  }

  return (
    <button
      type="button"
      onClick={pro.openPaywall}
      aria-label={`${meta.title} · Margin Pro`}
      className={`relative block w-full text-left ${className ?? ''}`}
    >
      <div className="pointer-events-none filter blur-[3px] opacity-60">
        {children}
      </div>
      <div className="absolute inset-0 flex items-center justify-center">
        <div className="inline-flex items-center gap-2 rounded-pill bg-surface px-3 py-2 shadow-lg">
          <Lock size={12} strokeWidth={1.75} className="text-text-tertiary" />
          <Sparkles size={12} strokeWidth={1.75} className="text-warning" />
          <span className="text-[12px] font-semibold text-text-primary">
            Margin Pro
          </span>
          <span className="text-[12px] text-text-secondary">
            · {meta.title}
          </span>
        </div>
      </div>
    </button>
  );
}
